import { DashboardLocationCard } from '../dashboardTypes'

export type LocationGroupBy = 'country' | 'region'

export const OTHER_REGION = 'Other'

export interface LocationCountrySection {
  country: string
  title: string
  description: string
  worksites: number
  employees: number
  enrolled: number
  cities: number
  cards: DashboardLocationCard[]
}

const regionNames =
  typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null

export const countryTitle = (country: string): string => {
  const code = country.trim()
  if (!code) {
    return 'Unknown'
  }
  try {
    return regionNames?.of(code) || code
  } catch {
    return code
  }
}

const descriptionFor = (cards: DashboardLocationCard[]): string => {
  const cities = cards.map((card) => card.city).filter(Boolean)
  if (cities.length <= 3) {
    return cities.join(' · ')
  }
  return `${cities.slice(0, 3).join(' · ')} · +${cities.length - 3} more`
}

const regionFor = (card: DashboardLocationCard): string => {
  const continents = card.worksites.map((site) => site.continent?.trim() || '')
  if (
    continents.length > 0 &&
    continents.every((value) => value && value === continents[0])
  ) {
    return continents[0]
  }
  return OTHER_REGION
}

const groupKey = (card: DashboardLocationCard, groupBy: LocationGroupBy) =>
  groupBy === 'region' ? regionFor(card) : card.country.trim() || 'UNKNOWN'

export const groupLocationCountries = (
  cards: DashboardLocationCard[],
  groupBy: LocationGroupBy = 'country'
): LocationCountrySection[] => {
  const grouped = new Map<string, DashboardLocationCard[]>()
  cards.forEach((card) => {
    const key = groupKey(card, groupBy)
    const bucket = grouped.get(key) ?? []
    bucket.push(card)
    grouped.set(key, bucket)
  })

  return [...grouped.entries()]
    .map(([country, sectionCards]) => {
      const cardsInCountry = [...sectionCards].sort(
        (a, b) =>
          b.employeeCount - a.employeeCount || a.city.localeCompare(b.city)
      )
      return {
        country,
        title:
          groupBy === 'region'
            ? country
            : countryTitle(country === 'UNKNOWN' ? '' : country),
        description: descriptionFor(cardsInCountry),
        worksites: cardsInCountry.reduce(
          (sum, card) => sum + card.worksites.length,
          0
        ),
        employees: cardsInCountry.reduce(
          (sum, card) => sum + card.employeeCount,
          0
        ),
        enrolled: cardsInCountry.reduce((sum, card) => sum + card.enrolled, 0),
        cities: cardsInCountry.length,
        cards: cardsInCountry,
      }
    })
    .sort((a, b) => {
      if (groupBy === 'region') {
        if (a.country === OTHER_REGION) return 1
        if (b.country === OTHER_REGION) return -1
      }
      return b.employees - a.employees || a.title.localeCompare(b.title)
    })
}
